// Google-Ads-Tab: Ads-Performance (Google/Facebook/TikTok) aus Metricool — Aggregate,
// 30T-Trend, verlässlicher Zeitraum (nach Tracking-Fix), Top-Kampagnen, Bleeder + Claude-Insight.
import type { MarketingDeps } from "./marketing.js";

interface Tot { spent: number; conversions: number; value: number; roas: number; ctr: number; cpc: number }
interface TrendPt { spent: number; impressions: number; conversions: number; roas: number }
interface Camp { name: string; spent: number; roas: number; conv?: number }
interface Reliable { since: string; spent: number; conversions: number; value: number; roas: number; cpc: number; campaigns: number }
interface Channel {
  label: string; campaigns: number; total: Tot;
  trend: { last30: TrendPt; prev30: TrendPt; impressionsDropPct: number };
  top: Camp[]; bleeders: Camp[]; reliable: Reliable | null;
}
interface AdsSummary {
  empty?: boolean; generated_at?: string; report_at?: string; adsValidFrom?: string | null;
  dataCaveat?: string; channels?: Record<string, Channel>;
  insight?: { summary: string; recommendations: string[] } | null;
}
interface Opp {
  id: number; campaign_id: string; campaign_name: string; ad_group_id: string | null;
  term: string; suggested_match: string; clicks: number; conversions: number; cost: number; ctr: number;
}

const eur = (n: number): string => (n ?? 0).toLocaleString("de-DE", { maximumFractionDigits: 0 }) + " €";
const num = (n: number): string => (n ?? 0).toLocaleString("de-DE", { maximumFractionDigits: 0 });
const x = (n: number): string => (n ?? 0).toLocaleString("de-DE", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

function el(tag: string, cls?: string, text?: string): HTMLElement {
  const e = document.createElement(tag); if (cls) e.className = cls; if (text != null) e.textContent = text; return e;
}
function table(head: string[], rows: Array<Array<string>>): HTMLTableElement {
  const t = document.createElement("table"); t.className = "om-table";
  const hr = t.createTHead().insertRow();
  for (const h of head) hr.appendChild(el("th", undefined, h));
  const tb = t.createTBody();
  for (const r of rows) { const tr = tb.insertRow(); for (const c of r) tr.insertCell().textContent = c; }
  return t;
}

async function actOpp(
  kind: "apply" | "reject", id: number, row: HTMLElement,
  bApply: HTMLButtonElement, bRej: HTMLButtonElement, deps: MarketingDeps,
): Promise<void> {
  bApply.disabled = true; bRej.disabled = true;
  row.classList.add("om-opp-busy");
  const r = await deps.api<{ ok?: boolean }>(`/api/marketing/ads/opportunities/${kind}`, "POST", { ids: [id] });
  row.classList.remove("om-opp-busy");
  if (r.status === 200 && r.data?.ok) {
    row.textContent = kind === "apply" ? "✓ Als Keyword übernommen" : "✗ Verworfen";
    row.className = "om-opp-row om-opp-done";
  } else {
    bApply.disabled = false; bRej.disabled = false;
    row.appendChild(el("span", "om-ads-warn", " Fehler – bitte erneut versuchen"));
  }
}

async function renderOpps(hostEl: HTMLElement, deps: MarketingDeps): Promise<void> {
  hostEl.textContent = "";
  const r = await deps.api<{ opportunities: Opp[] }>("/api/marketing/ads/opportunities");
  const opps = r.data?.opportunities ?? [];
  const det = document.createElement("details"); det.className = "perf-insight"; det.open = true;
  const sum = document.createElement("summary");
  sum.appendChild(el("span", "perf-insight__title", "🔍 Suchbegriff-Chancen"));
  sum.appendChild(el("span", "perf-insight__hint", opps.length ? `${opps.length} Vorschläge` : "keine offen"));
  det.appendChild(sum);
  const body = el("div", "perf-insight__body");
  if (!opps.length) {
    body.appendChild(el("p", "dim", "Keine offenen Keyword-Chancen. Der Montags-Lauf schlägt neue vor, sobald konvertierende Suchbegriffe auftauchen, die noch kein Keyword sind."));
  } else {
    body.appendChild(el("p", "dim", "Konvertierende/hoch-intente Suchbegriffe, die noch kein Keyword sind. „Übernehmen“ legt den Begriff als Keyword in der passenden Anzeigengruppe an."));
    const byCamp = new Map<string, Opp[]>();
    for (const o of opps) { let a = byCamp.get(o.campaign_name); if (!a) { a = []; byCamp.set(o.campaign_name, a); } a.push(o); }
    for (const [camp, list] of byCamp) {
      body.appendChild(el("h4", "om-ads-sub", camp));
      for (const o of list) {
        const row = el("div", "om-opp-row");
        const info = el("span", "om-opp-info");
        info.appendChild(el("span", "om-opp-match", o.suggested_match));
        info.appendChild(el("code", "om-opp-term", o.term));
        info.appendChild(el("span", "dim", ` ${(o.conversions ?? 0).toFixed(1)} Conv · ${o.clicks} Kl · ${eur(o.cost)}`));
        row.appendChild(info);
        const bApply = el("button", "btn-primary om-opp-btn", "Übernehmen") as HTMLButtonElement;
        const bRej = el("button", "btn-secondary om-opp-btn", "Verwerfen") as HTMLButtonElement;
        if (!o.ad_group_id) { bApply.disabled = true; bApply.title = "Keine Anzeigengruppe zugeordnet"; }
        bApply.addEventListener("click", () => { void actOpp("apply", o.id, row, bApply, bRej, deps); });
        bRej.addEventListener("click", () => { void actOpp("reject", o.id, row, bApply, bRej, deps); });
        const actions = el("span", "om-opp-actions"); actions.appendChild(bApply); actions.appendChild(bRej);
        row.appendChild(actions);
        body.appendChild(row);
      }
    }
  }
  det.appendChild(body);
  hostEl.appendChild(det);
}

export function initAdsTab(m: HTMLElement, deps: MarketingDeps): void {
  const host = m.querySelector("[data-ads]") as HTMLElement | null;
  const msg = m.querySelector('[data-msg="ads"]') as HTMLElement | null;
  if (!host) return;

  const load = async (): Promise<void> => {
    host.innerHTML = '<p class="dim">Lade …</p>';
    const r = await deps.api<AdsSummary>("/api/marketing/ads");
    const d = r.data;
    if (!d || d.empty) {
      host.innerHTML = '<p class="dim">Noch keine Ads-Daten. Über „↻ Ads-Daten ziehen“ starten (zieht Google/Facebook Ads aus Metricool).</p>';
      return;
    }
    host.textContent = "";

    // Tracking-Caveat-Banner
    const banner = el("div", "om-ads-caveat");
    if (d.adsValidFrom) {
      banner.classList.add("ok");
      banner.textContent = `✓ Conversions/ROAS verlässlich ab ${d.adsValidFrom} (Tracking-Fix). Ältere Zahlen waren durch mitgezählte Teilkonversionen aufgebläht.`;
    } else {
      banner.textContent = "⚠ Conversion-Tracking-Vorbehalt: Bis zum Tracking-Fix (Datum noch nicht gesetzt) wurden GTM-Teilkonversionen fälschlich als Conversions gezählt → Conversions/ROAS älterer Zeiträume sind aufgebläht und NICHT verlässlich. Liefermetriken (Impressionen/Klicks/Spend) sind valide.";
    }
    host.appendChild(banner);

    // Insight (aufklappbar, wie Performance-Tab)
    if (d.insight) {
      const det = document.createElement("details"); det.className = "perf-insight"; det.open = true;
      const sum = document.createElement("summary");
      sum.appendChild(el("span", "perf-insight__title", "💡 Ads-Insight"));
      const when = d.report_at || d.generated_at;
      if (when) sum.appendChild(el("span", "perf-insight__date", "Stand " + new Date(when).toLocaleDateString("de-DE", { day: "2-digit", month: "long", year: "numeric" })));
      const rc = d.insight.recommendations?.length ?? 0;
      sum.appendChild(el("span", "perf-insight__hint", rc ? `${rc} Empfehlungen` : "ansehen"));
      det.appendChild(sum);
      const body = el("div", "perf-insight__body");
      body.appendChild(el("p", undefined, d.insight.summary));
      if (rc) { const ul = document.createElement("ul"); for (const rec of d.insight.recommendations) ul.appendChild(el("li", undefined, rec)); body.appendChild(ul); }
      det.appendChild(body);
      host.appendChild(det);
    }

    // Suchbegriff-Chancen (Keyword-Vorschläge je Kampagne, per Klick übernehmbar)
    const oppHost = el("div", "om-ads-opps");
    host.appendChild(oppHost);
    void renderOpps(oppHost, deps);

    // Pro Kanal
    const CTX: Record<string, string> = { "Google Ads": "SWAV · Online-Ausbildungen (Ziel: profitabel)" };
    const LOGO: Record<string, string> = {
      "Google Ads": '<svg viewBox="0 0 48 48" width="18" height="18" aria-hidden="true"><path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"/><path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"/><path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"/><path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"/></svg>',
    };
    for (const ch of Object.values(d.channels ?? {})) {
      const h = el("h3", "om-ads-h");
      const logo = LOGO[ch.label]; if (logo) { const ic = el("span", "om-ads-logo"); ic.innerHTML = logo; h.appendChild(ic); }
      h.appendChild(document.createTextNode(ch.label));
      const cx = CTX[ch.label]; if (cx) h.appendChild(el("span", "om-ads-ctx", " — " + cx));
      host.appendChild(h);
      const t = ch.trend; const drop = t.impressionsDropPct;
      const meta = el("p", "dim",
        `${ch.campaigns} Kampagnen · 365T: Spend ${eur(ch.total.spent)} · ROAS ${x(ch.total.roas)} · CTR ${x(ch.total.ctr)} % · CPC ${x(ch.total.cpc)} €`);
      host.appendChild(meta);

      if (ch.reliable) {
        host.appendChild(el("p", "om-ads-reliable",
          `Verlässlich (ab ${ch.reliable.since}): Spend ${eur(ch.reliable.spent)} · ${num(ch.reliable.conversions)} Conv · ROAS ${x(ch.reliable.roas)} · CPC ${x(ch.reliable.cpc)} €`));
      }

      // Trend 30T vs Vor-30T
      const tr = table(["Zeitraum", "Spend", "Impressionen", "Conversions", "ROAS"], [
        ["letzte 30 T", eur(t.last30.spent), num(t.last30.impressions), num(t.last30.conversions), x(t.last30.roas)],
        ["vorherige 30 T", eur(t.prev30.spent), num(t.prev30.impressions), num(t.prev30.conversions), x(t.prev30.roas)],
      ]);
      host.appendChild(tr);
      if (drop >= 40) {
        host.appendChild(el("p", "om-ads-warn",
          `⚠ Impressionen −${drop} % vs. Vormonat → wahrscheinlich Auslieferungs-Drosselung (Ziel-ROAS zu hoch fürs reale Conversion-Niveau).`));
      }

      if (ch.top?.length) {
        host.appendChild(el("h4", "om-ads-sub", "Top nach Spend"));
        host.appendChild(table(["Kampagne", "Spend", "ROAS", "Conv."],
          ch.top.map(c => [c.name, eur(c.spent), x(c.roas), num(c.conv ?? 0)])));
      }
      if (ch.bleeders?.length) {
        host.appendChild(el("h4", "om-ads-sub", "Verlustbringer (Spend > 500 €, ROAS < 0,5)"));
        host.appendChild(table(["Kampagne", "Spend", "ROAS"],
          ch.bleeders.map(c => [c.name, eur(c.spent), x(c.roas)])));
      }
    }
  };

  m.querySelector('[data-action="run-ads"]')?.addEventListener("click", () => {
    void (async () => {
      if (msg) msg.textContent = "Starte Ads-Pull …";
      const r = await deps.api<{ stamp?: string }>("/api/marketing/motors/ads/run", "POST");
      if (msg) msg.textContent = r.status === 200 ? "Läuft — Tab in ~1 Min neu öffnen." : r.status === 409 ? "Läuft bereits." : `Fehler (${r.status})`;
    })();
  });

  // Beim Öffnen des Tabs den neuen Montags-Pull als gelesen markieren + Badge löschen.
  const tabBtn = m.querySelector('[data-tab="ads"]');
  tabBtn?.addEventListener("click", () => {
    void deps.api("/api/marketing/ads/seen", "POST");
    const badge = tabBtn.querySelector(".tab-badge") as HTMLElement | null;
    if (badge) { badge.textContent = "0"; badge.hidden = true; }
  });

  void load();
}
